import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, Unlock, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export function LoginActivity() {
  const [data, setData] = useState({ events: [], locked: [] });

  const load = useCallback(() => api.get("/admin/logins").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const unlock = async (email) => {
    try { await api.post("/admin/unlock", { email }); toast.success(`${email} unlocked`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="p-6 mt-4" data-testid="login-activity">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="font-head text-lg font-700">Sign-in history</h3>
        <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-logins-button" className="gap-1.5"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Last 200 sign-in attempts. Accounts lock for 15 minutes after 5 wrong passwords.</p>

      {data.locked.length > 0 && (
        <div className="space-y-2 mb-5" data-testid="locked-accounts">
          {data.locked.map((l) => (
            <div key={l.email} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/50 bg-destructive/5 px-4 py-2.5" data-testid={`locked-${l.email}`}>
              <span className="flex items-center gap-2 text-sm min-w-0"><Lock className="h-4 w-4 text-destructive shrink-0" /><span className="font-medium break-all">{l.email}</span><span className="text-muted-foreground">locked until {fmt(l.locked_until)}</span></span>
              <Button size="sm" variant="outline" onClick={() => unlock(l.email)} data-testid={`unlock-${l.email}`} className="gap-1.5"><Unlock className="h-4 w-4" /> Unlock</Button>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-md border border-border max-h-96 overflow-auto divide-y divide-border">
        {data.events.length === 0 && <p className="text-sm text-muted-foreground p-4">No sign-in attempts recorded yet.</p>}
        {data.events.map((e) => (
          <div key={e.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`login-event-${e.id}`}>
            {e.success ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(var(--success))]" /> : <XCircle className="h-4 w-4 shrink-0 text-destructive" />}
            <div className="min-w-0 flex-1">
              <p className="font-medium break-all">{e.email}</p>
              <p className="text-xs text-muted-foreground">{e.reason}{e.ip ? ` · ${e.ip}` : ""}</p>
            </div>
            <Badge variant="outline" className="rounded-full shrink-0 whitespace-nowrap">{fmt(e.at)}</Badge>
          </div>
        ))}
      </div>
    </Card>
  );
}
